import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import Container from '../Container/Container'

import * as styles from './ForgotPassword.m.css'

enum ForgotPasswordStep {
  WhatsNeeded,
  ForgotUsername,
  ForgotPassword,
  Finished,
}

const ForgotPassword: React.FC = (): React.ReactElement => {
  const navigate = useNavigate()
  const [remembersUsername, setRemembersUsername] = useState<boolean>(true)
  const [forgotPasswordStep, setForgotPasswordStep] =
    useState<ForgotPasswordStep>(ForgotPasswordStep.WhatsNeeded)

  const [username, setUsername] = useState<string>('')
  const [email, setEmail] = useState<string>('')

  const handleRememberUsernameFormSubmit = (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault()

    if (remembersUsername) {
      setForgotPasswordStep(ForgotPasswordStep.ForgotPassword)
    } else {
      setForgotPasswordStep(ForgotPasswordStep.ForgotUsername)
    }

    return false
  }

  const handleForgotUsernameSubmit: React.FormEventHandler<
    HTMLFormElement
  > = async (e) => {
    e.preventDefault()

    await fetch('/auth/forgot-username', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    })

    setForgotPasswordStep(ForgotPasswordStep.Finished)

    return false
  }

  const handleForgotPasswordSubmit: React.FormEventHandler<
    HTMLFormElement
  > = async (e) => {
    e.preventDefault()

    await fetch('/auth/reset-codes', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username }),
    })

    setForgotPasswordStep(ForgotPasswordStep.Finished)
  }

  const handleBackButtonClick: React.MouseEventHandler<HTMLButtonElement> = (
    e,
  ) => {
    e.preventDefault()

    setForgotPasswordStep(ForgotPasswordStep.WhatsNeeded)
  }

  const returnToLogin = () => {
    navigate('/')
  }

  return (
    <Container>
      <h1>Forgot Username or Password</h1>
      {forgotPasswordStep === ForgotPasswordStep.WhatsNeeded && (
        <>
          <p>Do you remember your username?</p>
          <form onSubmit={handleRememberUsernameFormSubmit}>
            <div>
              <label>
                <input
                  type="radio"
                  name="remember"
                  value="yes"
                  onChange={() => setRemembersUsername(true)}
                  checked={remembersUsername}
                />
                Yes
              </label>
            </div>
            <div>
              <label>
                <input
                  type="radio"
                  name="remember"
                  value="no"
                  onChange={() => setRemembersUsername(false)}
                  checked={!remembersUsername}
                />
                No
              </label>
            </div>
            <div className={styles.buttonContainer}>
              <button type="submit">Next</button>
            </div>
          </form>
        </>
      )}
      {forgotPasswordStep === ForgotPasswordStep.ForgotUsername && (
        <>
          <p>
            Enter your email address and we will send you the usernames
            associated with your account. Once you've identified your username,
            you will be able to reset your password.
          </p>
          <form
            className={styles.resetPasswordForm}
            onSubmit={handleForgotUsernameSubmit}
          >
            <input
              type="email"
              placeholder="Email Address"
              onChange={(e) => setEmail(e.target.value)}
            />
            <div className={styles.buttonContainer}>
              <button
                className={styles.backButton}
                onClick={handleBackButtonClick}
              >
                Back
              </button>
              <button type="submit">Submit</button>
            </div>
          </form>
        </>
      )}
      {forgotPasswordStep === ForgotPasswordStep.ForgotPassword && (
        <>
          <p>Enter your username and we will send you a password reset link.</p>
          <p>The link in the email will expire after 10 minutes.</p>
          <form
            className={styles.resetPasswordForm}
            onSubmit={handleForgotPasswordSubmit}
          >
            <input
              type="text"
              placeholder="Username"
              onChange={(e) => setUsername(e.target.value)}
            />
            <div className={styles.buttonContainer}>
              <button
                className={styles.backButton}
                onClick={handleBackButtonClick}
              >
                Back
              </button>
              <button type="submit">Submit</button>
            </div>
          </form>
        </>
      )}
      {forgotPasswordStep === ForgotPasswordStep.Finished && (
        <p>
          An email was sent with further instructions. Please check your inbox.
        </p>
      )}
      <div className={styles.returnToLogin}>
        <a onClick={returnToLogin}>Return to Login</a>
      </div>
    </Container>
  )
}

export default ForgotPassword
